import { z } from 'zod'

/**
 * Filtros nomeados reutilizaveis em qualquer tela.
 *
 * Um filtro e um conjunto de criterios (campo + operador + valor) unidos por
 * E/OU. As regras de avaliacao vivem aqui, puras: a UI (`FilterBar` de
 * @orbind/ui) so monta o objeto e a tela aplica com `evaluateFilter`.
 */

/** Tipo do campo — define quais operadores fazem sentido e como pedir o valor. */
export const filterFieldTypeSchema = z.enum(['text', 'select', 'number', 'boolean', 'date'])
export type FilterFieldType = z.infer<typeof filterFieldTypeSchema>

export const filterOperatorSchema = z.enum([
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'eq',
  'neq',
  'in',
  'not_in',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'before',
  'after',
  'is_empty',
  'is_not_empty',
])
export type FilterOperator = z.infer<typeof filterOperatorSchema>

export const filterCriterionSchema = z.object({
  id: z.string().min(1),
  /** Chave do campo na definicao passada para a UI. */
  field: z.string().min(1),
  operator: filterOperatorSchema,
  /** string para texto/numero/data, string[] para in/not_in e between. */
  value: z.union([z.string(), z.array(z.string()), z.null()]),
})
export type FilterCriterion = z.infer<typeof filterCriterionSchema>

export const savedFilterSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(60),
  /** Como os criterios se combinam. */
  join: z.enum(['AND', 'OR']),
  criteria: z.array(filterCriterionSchema),
  /**
   * Onde o filtro aparece na tela (2026-09-02): 'button' = chip na barra
   * (padrao); 'list' = so na LISTA de filtros ao lado da busca do quadro.
   */
  display: z.enum(['button', 'list']).optional(),
  /**
   * Colunas da lista com este filtro em uso (SPEC-20261103-1217): quais e em
   * que ordem. Ausente = o filtro usa as colunas do "sem filtro".
   */
  columns: z.array(z.string()).optional(),
})
export type SavedFilter = z.infer<typeof savedFilterSchema>

/** Operadores oferecidos por tipo de campo, na ordem em que aparecem. */
export const OPERATORS_BY_TYPE: Record<FilterFieldType, readonly FilterOperator[]> = {
  text: ['contains', 'not_contains', 'eq', 'neq', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  select: ['in', 'not_in', 'is_empty', 'is_not_empty'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'],
  boolean: ['eq'],
  date: ['eq', 'before', 'after', 'between', 'is_empty', 'is_not_empty'],
}

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  contains: 'contém',
  not_contains: 'não contém',
  starts_with: 'começa com',
  ends_with: 'termina com',
  eq: 'é igual a',
  neq: 'é diferente de',
  in: 'é um de',
  not_in: 'não é nenhum de',
  gt: 'maior que',
  gte: 'maior ou igual a',
  lt: 'menor que',
  lte: 'menor ou igual a',
  between: 'entre',
  before: 'antes de',
  after: 'depois de',
  is_empty: 'está vazio',
  is_not_empty: 'não está vazio',
}

export function operatorLabel(operator: FilterOperator): string {
  return OPERATOR_LABELS[operator]
}

/** Operadores que dispensam valor. */
export function operatorNeedsValue(operator: FilterOperator): boolean {
  return operator !== 'is_empty' && operator !== 'is_not_empty'
}

/** Comparacao de texto sem acento e sem caixa — busca em pt-BR sem surpresa. */
const norm = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

const asNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const asTime = (value: unknown) => {
  const time = new Date(String(value)).getTime()
  return Number.isNaN(time) ? null : time
}

const isEmpty = (value: unknown) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

/**
 * Avalia um criterio contra o valor do registro.
 *
 * `recordValue` pode ser um array (ex.: areas de um quadro) — nesse caso
 * `in`/`not_in` consideram intersecao e os operadores de texto valem se
 * qualquer elemento casar.
 */
export function evaluateCriterion(recordValue: unknown, criterion: FilterCriterion): boolean {
  const { operator, value } = criterion

  if (operator === 'is_empty') return isEmpty(recordValue)
  if (operator === 'is_not_empty') return !isEmpty(recordValue)

  const wanted = Array.isArray(value) ? value : value === null ? [] : [value]
  // criterio sem valor nao filtra nada (rascunho ainda incompleto)
  if (wanted.length === 0) return true

  const actuals = Array.isArray(recordValue) ? recordValue : [recordValue]

  switch (operator) {
    case 'in':
      return actuals.some((actual) => wanted.some((w) => norm(w) === norm(actual)))
    case 'not_in':
      return !actuals.some((actual) => wanted.some((w) => norm(w) === norm(actual)))
    case 'eq':
      return actuals.some((actual) => norm(actual) === norm(wanted[0]))
    case 'neq':
      return !actuals.some((actual) => norm(actual) === norm(wanted[0]))
    case 'contains':
      return actuals.some((actual) => norm(actual).includes(norm(wanted[0])))
    case 'not_contains':
      return !actuals.some((actual) => norm(actual).includes(norm(wanted[0])))
    case 'starts_with':
      return actuals.some((actual) => norm(actual).startsWith(norm(wanted[0])))
    case 'ends_with':
      return actuals.some((actual) => norm(actual).endsWith(norm(wanted[0])))
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const limit = asNumber(wanted[0])
      if (limit === null) return true
      return actuals.some((actual) => {
        const current = asNumber(actual)
        if (current === null) return false
        if (operator === 'gt') return current > limit
        if (operator === 'gte') return current >= limit
        if (operator === 'lt') return current < limit
        return current <= limit
      })
    }
    case 'between': {
      const [rawFrom, rawTo] = wanted
      const numericFrom = asNumber(rawFrom)
      const numericTo = asNumber(rawTo)
      return actuals.some((actual) => {
        if (numericFrom !== null && numericTo !== null) {
          const current = asNumber(actual)
          return current !== null && current >= numericFrom && current <= numericTo
        }
        const current = asTime(actual)
        const from = asTime(rawFrom)
        const to = asTime(rawTo)
        if (current === null || from === null || to === null) return false
        return current >= from && current <= to
      })
    }
    case 'before':
    case 'after': {
      const limit = asTime(wanted[0])
      if (limit === null) return true
      return actuals.some((actual) => {
        const current = asTime(actual)
        if (current === null) return false
        return operator === 'before' ? current < limit : current > limit
      })
    }
    default:
      return true
  }
}

/**
 * Aplica o filtro inteiro a um registro ja "achatado" em `{ campo: valor }`.
 * Filtro sem criterios nao restringe nada.
 */
export function evaluateFilter(values: Record<string, unknown>, filter: SavedFilter): boolean {
  if (filter.criteria.length === 0) return true
  const results = filter.criteria.map((criterion) => evaluateCriterion(values[criterion.field], criterion))
  return filter.join === 'AND' ? results.every(Boolean) : results.some(Boolean)
}
